import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { currentHeaderHeightPx } from 'src/components/layout/redesign/header-shared/header-styling';

const SCROLL_BELOW_HEADER_BEFORE_HIDE_PX = 100;

export const useRibbonEffects = (defaultHeight = 56) => {
  const [shouldShowExpand, setShowExpand] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldHide, setShouldHide] = useState(false);
  const [hasRecentlyChangedShouldHide, setHasRecentlyChangedShouldHide] = useState(false);
  const [componentHeight, setComponentHeight] = useState(defaultHeight);

  const componentRef = useRef<HTMLDivElement | null>(null);
  const textAndChevronContainerRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLDivElement | null>(null);
  const chevronRef = useRef<HTMLDivElement | null>(null);

  const updateShouldShowExpand = useCallback(() => {
    if (!textRef.current || !textAndChevronContainerRef.current) {
      return;
    }

    if (!isExpanded) {
      setShowExpand(textRef.current.scrollWidth >= textAndChevronContainerRef.current.offsetWidth);
      return;
    }

    if (!chevronRef.current) {
      return;
    }

    const isTextOnOneLine = textRef.current.scrollHeight <= chevronRef.current.scrollHeight;

    if (isTextOnOneLine) {
      setShowExpand(false);
      setIsExpanded(false);
    }
  }, [isExpanded]);

  const updateShouldHide = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    setShouldHide(window.scrollY - currentHeaderHeightPx() > SCROLL_BELOW_HEADER_BEFORE_HIDE_PX);
  }, []);

  const updateComponentHeight = useCallback(() => {
    setComponentHeight(componentRef.current?.getBoundingClientRect().height || defaultHeight);
  }, []);

  useLayoutEffect(() => {
    updateShouldHide();
    updateShouldShowExpand();
    updateComponentHeight();
  }, [updateShouldHide, updateShouldShowExpand, updateComponentHeight]);

  useEffect(() => {
    setHasRecentlyChangedShouldHide(true);
    setTimeout(() => setHasRecentlyChangedShouldHide(false), 400);
  }, [shouldHide]);

  useEffect(() => {
    const onWindowSizeChange = () => {
      updateShouldShowExpand();
      updateComponentHeight();
    };

    window.addEventListener('resize', onWindowSizeChange);
    window.addEventListener('deviceorientation', onWindowSizeChange);

    return () => {
      window.removeEventListener('resize', onWindowSizeChange);
      window.removeEventListener('deviceorientation', onWindowSizeChange);
    };
  }, [updateShouldShowExpand, updateComponentHeight]);

  useEffect(() => {
    // We don't want to update this value if it is animating as it can cause issues with the scroll position calculation.
    // We do a check when the animation is finished to check if the banner has ended in the correct state (i.e. from
    // the user repeatedly scrolling back and forth)
    if (hasRecentlyChangedShouldHide) {
      return;
    }

    updateShouldHide();
    window.addEventListener('scroll', updateShouldHide);

    return () => {
      window.removeEventListener('scroll', updateShouldHide);
    };
  }, [updateShouldHide, hasRecentlyChangedShouldHide]);

  return {
    shouldShowExpand,
    componentHeight,
    updateComponentHeight,
    isExpanded,
    shouldHide,
    setIsExpanded,
    componentRef,
    textAndChevronContainerRef,
    textRef,
    chevronRef,
  };
};
